import React, { useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'react-toastify';
import { Package, Plus, Search, Edit, Trash, Eye, X, Loader2, MoreVertical, RefreshCw
} from 'lucide-react';
import { useTheme } from '../../App';
import FormBuilder from './forms/FormBuilder';
import { moduloSchema } from './forms/schemas/moduloSchema';

// Componente DropdownMenu (igual que AdminEmpresas)
const DropdownMenu = ({ item, onEdit, onView, onDelete, isDarkMode }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const dropdownRef = useRef(null);
  
  const handleToggle = (e) => {
    if (!isOpen) {
      const rect = e.target.getBoundingClientRect();
      const menuHeight = 120;
      const menuWidth = 140;
      
      let top = rect.bottom + 8;
      let left = rect.right - menuWidth;
      
      if (top + menuHeight > window.innerHeight) {
        top = rect.top - menuHeight + 8;
      }
      
      if (left < 0) {
        left = rect.left;
      }
      
      setMenuPosition({ top, left });
    }
    setIsOpen(!isOpen);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={handleToggle}
        className={`p-1 rounded ${isDarkMode ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
        title="Acciones"
      >
        <MoreVertical size={16} />
      </button>
      
      {isOpen && (
        <>
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setIsOpen(false)}
          ></div>
          
          <div 
            className={`fixed z-50 rounded-lg shadow-lg py-1 min-w-[140px] ${
              isDarkMode ? 'bg-gray-700 border border-gray-600' : 'bg-white border border-gray-200'
            }`}
            style={{ top: `${menuPosition.top}px`, left: `${menuPosition.left}px` }}
          >
            <button
              onClick={() => {
                onView(item);
                setIsOpen(false);
              }}
              className={`w-full px-4 py-2 text-left flex items-center ${
                isDarkMode ? 'text-gray-300 hover:bg-gray-600' : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Eye size={14} className="mr-2" />
              Ver detalles
            </button>
            
            <button
              onClick={() => {
                onEdit(item);
                setIsOpen(false);
              }}
              className={`w-full px-4 py-2 text-left flex items-center ${
                isDarkMode ? 'text-blue-400 hover:bg-gray-600' : 'text-blue-600 hover:bg-blue-50'
              }`}
            >
              <Edit size={14} className="mr-2" />
              Editar
            </button>
            
            <button
              onClick={() => {
                onDelete(item);
                setIsOpen(false);
              }}
              className={`w-full px-4 py-2 text-left flex items-center ${
                isDarkMode ? 'text-red-400 hover:bg-gray-600' : 'text-red-600 hover:bg-red-50'
              }`}
            >
              <Trash size={14} className="mr-2" />
              Eliminar
            </button>
          </div>
        </>
      )}
    </div>
  );
};

const AdminModulos = () => {
  const { isDarkMode } = useTheme();
  const [modulos, setModulos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [selectedItem, setSelectedItem] = useState(null);
  const searchInputRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  
  const handleSearchChange = useCallback((e) => {
    const value = e.target.value;
    setSearchInput(value);
    
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      setSearchTerm(value);
    }, 300);
  }, []);

  useEffect(() => {
  if (searchInputRef.current && document.activeElement !== searchInputRef.current) {
    const inputElement = searchInputRef.current;
    const currentSelectionStart = inputElement.selectionStart;
    const currentSelectionEnd = inputElement.selectionEnd;
    setTimeout(() => {
      if (inputElement && inputElement.parentNode) {
        inputElement.focus();
        inputElement.setSelectionRange(currentSelectionStart, currentSelectionEnd);
      }
    }, 0);
    }
  }, [searchTerm]);

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    loadModulos();
  }, []);

  const loadModulos = async () => {
    setLoading(true);
    
    try {
      const token = sessionStorage.getItem('mapasac_access_token');
      const response = await fetch('/api/modulos', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();

      if (data.success) {
        setModulos(data.data.modulos || []);
      } else {
        console.error('Error API módulos:', data);
        toast.error('Error cargando módulos');
      }
    } catch (err) {
      toast.error('Error de conexión');
      console.error('Error cargando módulos:', err);
    } finally {
      setLoading(false);
    }
  };

  const getFilteredData = () => {
    if (!searchTerm) return modulos;
    return modulos.filter(item => 
      Object.values(item).some(value => {
        if (value === null || value === undefined) {
          return false;
        }
        const stringValue = String(value).toLowerCase();
        const searchTermLower = searchTerm.toLowerCase();      
        return stringValue.includes(searchTermLower);
      })
    );
  };

  const handleCreate = () => {
    setModalMode('create');
    setSelectedItem(null);
    setShowModal(true);
  };

  const handleEdit = (item) => {
    setModalMode('edit');
    setSelectedItem(item);
    setShowModal(true);
  };

  const handleView = (item) => {
    setModalMode('view');
    setSelectedItem(item);
    setShowModal(true);
  };

  const handleDelete = (item) => {
    setModalMode('delete');
    setSelectedItem(item);
    setShowModal(true);	
  };

  const handleSave = async (formData) => {
    const token = sessionStorage.getItem('mapasac_access_token');
    
    try {
      let url, method;
      
      if (modalMode === 'create') {
        url = '/api/modulos';
        method = 'POST';
      } else if (modalMode === 'edit') {
        url = `/api/modulos/${selectedItem?.idmodulos}`;
        method = 'PUT';
      } else if (modalMode === 'delete') {
        url = `/api/modulos/${selectedItem?.idmodulos}`;
        method = 'DELETE';
      }
      
      const response = await fetch(url, {
        method: method,
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: modalMode !== 'delete' ? JSON.stringify(formData) : undefined
      });
      
      const data = await response.json();
      
      if (data.success) {
        if (modalMode === 'create') {
          toast.success('Módulo creado correctamente');
        } else if (modalMode === 'edit') {
          toast.success('Módulo actualizado correctamente');
        } else if (modalMode === 'delete') {
          toast.success('Módulo eliminado correctamente');
        }
        setShowModal(false);
        setTimeout(() => {
          loadModulos();
        }, 100);
      } else {
        throw new Error(data.error?.message || data.error || 'Error en la operación');
      }
      
    } catch (error) {
      console.error('Error en handleSave:', error);
      throw error; // FormBuilder manejará el error
    }
  };

  const getStatusBadge = (status) => {
    if (status === 1) {
      return <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs">Activo</span>;
    }
    return <span className="px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs">Inactivo</span>;
  };

  const handleActualizar = () => {
    setSearchTerm('');
    setSearchInput('');
    loadModulos();
    setTimeout(() => {
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }, 100);
  };

  // Toolbar Component
  const Toolbar = () => (
    <div className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border-b px-6 py-4`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4 flex-1">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
            <input
              ref={searchInputRef}
              type="text"
              value={searchInput}
              onChange={handleSearchChange}
              className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                isDarkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900'
              }`} 
              placeholder="Buscar en módulos..."
              style={{ fontSize: '16px' }}
              autoComplete="off"
            />
            {searchInput && (
              <button
                onClick={() => {
                  setSearchInput('');
                  setSearchTerm('');
                  if (searchInputRef.current) {
                    searchInputRef.current.focus();
                  }
                }}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                title="Limpiar búsqueda"
              >
                <X size={16} />
              </button>
            )}
          </div>
        </div>
        
        <div className="flex items-center space-x-3">
          <button 
            onClick={handleActualizar}
            className={`flex items-center space-x-2 px-4 py-2 border rounded-lg transition-colors ${
              isDarkMode ? 'text-gray-300 border-gray-600 hover:bg-gray-700' : 'text-gray-600 border-gray-300 hover:bg-gray-50'
            }`}
            title="Actualizar datos y limpiar filtros"
          >
            <RefreshCw size={16} />
            <span>Actualizar</span>
          </button>
          <button 
            onClick={handleCreate}
            className={`flex items-center space-x-2 px-4 py-2 border rounded-lg transition-colors ${
              isDarkMode ? 'text-gray-300 border-gray-600 hover:bg-gray-700' : 'text-gray-600 border-gray-300 hover:bg-gray-50'
            }`}
          >
            <Plus size={16} />
            <span>Crear</span>
          </button>
        </div>
      </div>
    </div>
  );

  // Módulos Table Component
  const ModulosTable = () => {
    const data = getFilteredData();
    return (
      <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} overflow-hidden`}>
        <div className="overflow-y-auto" style={{ maxHeight: 'calc(100vh / var(--ui-escala, 1) - 270px)' }}> 
          <table className="min-w-full divide-y divide-gray-200">
            <thead className={`${isDarkMode ? 'bg-gray-700' : 'bg-blue-50'} sticky top-0 z-10`}>
              <tr>
                <th className={`px-4 py-2 text-left text-xs font-medium uppercase tracking-wider ${
                  isDarkMode ? 'text-gray-300' : 'text-blue-700'}`}>Código</th>
                <th className={`px-4 py-2 text-left text-xs font-medium uppercase tracking-wider ${
                  isDarkMode ? 'text-gray-300' : 'text-blue-700'}`}>Nombre</th>
                <th className={`px-4 py-2 text-left text-xs font-medium uppercase tracking-wider ${
                  isDarkMode ? 'text-gray-300' : 'text-blue-700'}`}>Icono</th>  
                <th className={`px-4 py-2 text-left text-xs font-medium uppercase tracking-wider ${
                  isDarkMode ? 'text-gray-300' : 'text-blue-700'}`}>Descripción</th>
                <th className={`px-4 py-2 text-left text-xs font-medium uppercase tracking-wider ${
                  isDarkMode ? 'text-gray-300' : 'text-blue-700'}`}>Estado</th>
                <th className={`px-4 py-2 text-left text-xs font-medium uppercase tracking-wider ${
                  isDarkMode ? 'text-gray-300' : 'text-blue-700'}`}>Fecha Alta</th>
                <th className={`px-4 py-2 text-right text-xs font-medium uppercase tracking-wider ${
                  isDarkMode ? 'text-gray-300' : 'text-blue-700'}`}>Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {data.map((modulo, index) => (
                <tr key={modulo.idmodulos} className={`${
                  isDarkMode 
                    ? `hover:bg-gray-700 ${index % 2 === 0 ? 'bg-gray-800' : 'bg-gray-750'}` 
                    : `hover:bg-blue-50 ${index % 2 === 0 ? 'bg-white' : 'bg-blue-50'}`
                }`}>
                  <td className="px-4 py-2 whitespace-nowrap">
                    <div className="flex items-center">
                      <Package className="w-5 h-5 text-blue-600 mr-3" />
                      <div>
                        <div className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                          {modulo.codigo}
                        </div>
                        <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          ID: {modulo.idmodulos}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-2">
                    <div className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                      {modulo.nombre}
                    </div>
                  </td>
                  <td className="px-4 py-2">
                    <span className={`text-sm font-mono ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                      {modulo.icono}
                    </span>
                  </td>
                  <td className="px-4 py-2">
                    <div className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                      {modulo.descripcion}
                    </div>
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap">
                    {getStatusBadge(modulo.activo)}
                  </td>
                  <td className={`px-4 py-2 whitespace-nowrap text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                    {new Date(modulo.fecha_creacion).toLocaleDateString('es-ES')}
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end">
                      <DropdownMenu 
                        item={modulo}
                        onEdit={handleEdit}
                        onView={handleView}
                        onDelete={handleDelete}
                        isDarkMode={isDarkMode}
                        isNearEnd={index >= data.length - 2}
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <Toolbar />      
      <div className="flex-1 p-6 pb-2">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="animate-spin" size={32} />
            <span className="ml-2 text-gray-600">Cargando módulos...</span>
          </div>
        ) : (
          <ModulosTable />
        )}
      </div>      
      <div className="px-6 py-4">
        <div className={`flex items-center justify-between text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          <span>Mostrando {getFilteredData().length} de {modulos.length} registros</span>
          <div className={`flex items-center space-x-2 font-medium ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
            <span>💪 Powered by MAPASAC IT SYSTEMS</span>
          </div>
          <span>Última actualización: {new Date().toLocaleTimeString()}</span>
        </div>
      </div>      
      {/* FormBuilder Modal */}
      {showModal && (
        <FormBuilder
          schema={moduloSchema}
          mode={modalMode}
          initialData={selectedItem}
          onSave={handleSave}
          onClose={() => setShowModal(false)}
          isDarkMode={isDarkMode}
          title="Módulo"
          apiEndpoint="/api/modulos"
        />
      )}
    </div>
  );
};

export default AdminModulos;